import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Section,
  SectionSlot,
  SlotLabel,
  PreferenceLabel,
} from '@youshift/shared/types';
import {
  getFirstDayOfWeek,
  getWeekdayInitials,
  localeNormalizer,
  dateToString,
} from '@youshift/shared/utils';

import i18n from '../../../../utils/i18n';
import { YSButton } from '../../../../components/Buttons';
import { LabelBadge } from '../../../../components/Calendars/LabelBadge';
import { CompactSectionSlotChip } from '../../../../components/Calendars/SectionSlotChip';
import { useItrContext } from '../../../../layouts/IterationRootLayout/IterationRootLayout';

interface PreferenceSlotSelectorProps {
  selectedSlots: Set<number>;
  selectedPrefLabel: number | null;
  setSelectedSlots: (selectedSlots: Set<number>) => void;
  setSelectedPrefLabel: (selectedPrefLabel: number | null) => void;
  orphanSlots: Record<number, SectionSlot>;
}

export function PreferenceSlotSelector({
  orphanSlots,
  selectedSlots,
  selectedPrefLabel,
  setSelectedSlots,
  setSelectedPrefLabel,
}: PreferenceSlotSelectorProps) {
  const { sectionsWithSlots, sectionSlotsDict, prefLabels } = useItrContext();
  const { t } = useTranslation();
  const normalizedLocale = localeNormalizer(i18n.language);
  const daysOfWeek = getWeekdayInitials(t, normalizedLocale);

  const sections: Record<number, Section> = useMemo(
    () =>
      sectionsWithSlots.reduce<Record<number, Section>>((acc, { section }) => {
        acc[section.id_section] = section;
        return acc;
      }, {}),
    [sectionsWithSlots],
  );

  const [selectedSections, setSelectedSections] = useState<Set<number>>(
    new Set(Object.keys(sections).map(Number)),
  );
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState<Set<number>>(
    new Set(Object.keys(daysOfWeek).map(Number)),
  );
  const [selectedTimeFrames, setSelectedTimeFrames] = useState<Set<string>>(
    new Set(),
  );

  // Calculate unique timeframes based on selected sections, days, and labels
  const sectionSlotsUniqueTimeFrames = useMemo(() => {
    const timeFrames: Set<string> = new Set();
    Object.values(sectionSlotsDict || {})
      .filter(sectionSlot => orphanSlots[sectionSlot.id_section_slot])
      .forEach(sectionSlot => {
        // Check if the section is selected
        const isSectionSelected = selectedSections.has(sectionSlot.id_section);

        // Check if the day of week is selected
        const slotStart = new Date(sectionSlot.start);
        const slotDayOfWeek = slotStart.getUTCDay();
        const isDayOfWeekSelected = selectedDaysOfWeek.has(slotDayOfWeek);

        // Only add timeframe if section, day of week, and label are selected
        if (isSectionSelected && isDayOfWeekSelected) {
          timeFrames.add(
            `${dateToString(sectionSlot.start, 'hh:mm', normalizedLocale)} - ${dateToString(sectionSlot.end, 'hh:mm', normalizedLocale)}`,
          );
        }
      });
    return Array.from(timeFrames).sort((a, b) => {
      const [aStart, aEnd] = a.split(' - ');
      const [bStart, bEnd] = b.split(' - ');

      // Convert time strings to Date objects for proper comparison
      const today = new Date();
      const aStartDate = new Date(`${today.toDateString()} ${aStart}`);
      const bStartDate = new Date(`${today.toDateString()} ${bStart}`);

      // First sort by start time
      const startComparison = aStartDate.getTime() - bStartDate.getTime();

      // If start times are the same, compare end times
      if (startComparison === 0) {
        const aEndDate = new Date(`${today.toDateString()} ${aEnd}`);
        const bEndDate = new Date(`${today.toDateString()} ${bEnd}`);
        return aEndDate.getTime() - bEndDate.getTime();
      }

      return startComparison;
    });
  }, [
    sectionSlotsDict,
    normalizedLocale,
    selectedSections,
    selectedDaysOfWeek,
    orphanSlots,
  ]);

  // Handle smart selection of section slots
  const handleSmartSectionSlotsSelector = () => {
    const sectionSlotsToAdd = new Set(
      Object.keys(sectionSlotsDict)
        .filter(sectionSlotId => orphanSlots[Number(sectionSlotId)])
        .filter(sectionSlotId => {
          const sectionSlot = sectionSlotsDict[Number(sectionSlotId)];
          const slotStart = new Date(sectionSlot.start);
          const slotDayOfWeek = slotStart.getUTCDay();
          const isSlotInSelectedDaysOfWeek =
            selectedDaysOfWeek.has(slotDayOfWeek);
          const isSlotInSelectedTimeFrames = selectedTimeFrames.has(
            `${dateToString(sectionSlot.start, 'hh:mm', normalizedLocale)} - ${dateToString(sectionSlot.end, 'hh:mm', normalizedLocale)}`,
          );
          const isSlotInSelectedSection = selectedSections.has(
            sectionSlot.id_section,
          );

          return (
            isSlotInSelectedDaysOfWeek &&
            isSlotInSelectedTimeFrames &&
            isSlotInSelectedSection
          );
        })
        .map(Number),
    );
    setSelectedSlots(sectionSlotsToAdd);
  };

  return (
    <div className="space-y-2">
      {/* Preference labels selection */}
      {prefLabels && Object.keys(prefLabels).length > 0 && (
        <>
          <label className="block text-gray-700">
            1. {t('manager.preferencesConfig.selectPreferenceLabel')}
          </label>
          <p className="text-xs text-gray-600 mb-2">
            {t('manager.preferencesConfig.selectPreferenceLabelDescription')}
          </p>
          <div className="flex flex-wrap gap-2 mb-3 ml-2">
            {Object.values(prefLabels).map((label: PreferenceLabel) => (
              <span
                key={label.id_pref_label}
                className="inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200"
              >
                <input
                  type="radio"
                  name="prefLabel"
                  checked={selectedPrefLabel === label.id_pref_label}
                  onChange={() => setSelectedPrefLabel(label.id_pref_label)}
                  className="h-4 w-4 rounded-full border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <LabelBadge icon={label.icon} labelColor={label.color} />
                <span>{label.name}</span>
              </span>
            ))}
          </div>
        </>
      )}

      <div className="w-full h-px bg-gray-400 my-2" />
      <p className="text-gray-600 mb-2">
        2. {t('manager.preferencesConfig.selectSlotsForNewPrefSlots')}
      </p>

      {/* Sections selection */}
      {sections && Object.keys(sections).length > 1 && (
        <div className="ml-2">
          <div>
            <div className="flex flex-row gap-3 items-center">
              <label className="block text-sm text-gray-700 mb-1">
                {t('manager.preferencesConfig.sectionSelectionTitle')}
              </label>
              <button
                type="button"
                className="block text-blue-700 mb-1 text-xs hover:underline"
                onClick={() =>
                  setSelectedSections(
                    new Set(Object.keys(sections).map(Number)),
                  )
                }
              >
                {t('generic.selectAll')}
              </button>
              <button
                type="button"
                className="block text-red-700 mb-1 text-xs hover:underline"
                onClick={() => setSelectedSections(new Set())}
              >
                {t('generic.clear')}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {Object.entries(sections).map(([sectionId, section]) => (
              <span
                key={sectionId}
                className="inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200"
              >
                <input
                  type="checkbox"
                  checked={selectedSections.has(Number(sectionId))}
                  onChange={() => {
                    setSelectedSections(prev => {
                      const newSet = new Set(prev);
                      if (prev.has(Number(sectionId))) {
                        newSet.delete(Number(sectionId));
                      } else {
                        newSet.add(Number(sectionId));
                      }
                      return newSet;
                    });
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                {section.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Days of week selection */}
      <div className="ml-2">
        <div className="flex flex-row gap-3 items-center">
          <label className="block text-sm text-gray-700 mb-1">
            {t('manager.preferencesConfig.daysSelectionTitle')}
          </label>
          <button
            type="button"
            className="block text-blue-700 mb-1 text-xs hover:underline"
            onClick={() =>
              setSelectedDaysOfWeek(
                new Set(Object.keys(daysOfWeek).map(Number)),
              )
            }
          >
            {t('generic.selectAll')}
          </button>
          <button
            type="button"
            className="block text-red-700 mb-1 text-xs hover:underline"
            onClick={() => setSelectedDaysOfWeek(new Set())}
          >
            {t('generic.clear')}
          </button>
        </div>
      </div>
      <div className="flex space-x-2 mb-3 ml-2">
        {daysOfWeek.map((day, index) => (
          <span
            key={index}
            className="inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200"
          >
            <input
              type="checkbox"
              checked={selectedDaysOfWeek.has(
                getFirstDayOfWeek(normalizedLocale) === 1
                  ? (index + 1) % 7
                  : index,
              )}
              onChange={() =>
                setSelectedDaysOfWeek(prev => {
                  const newSet = new Set(prev);
                  const normalizedIndex =
                    getFirstDayOfWeek(normalizedLocale) === 1
                      ? (index + 1) % 7
                      : index;
                  if (prev.has(normalizedIndex)) {
                    newSet.delete(normalizedIndex);
                  } else {
                    newSet.add(normalizedIndex);
                  }
                  return newSet;
                })
              }
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            {day}
          </span>
        ))}
      </div>

      {/* Time frames selection */}
      <div className="ml-2">
        <div className="flex flex-row gap-3 items-center">
          <label className="block text-sm text-gray-700 mb-1">
            {t('manager.preferencesConfig.timeFrameSelectionTitle')}
          </label>
          <button
            type="button"
            className="block text-blue-700 mb-1 text-xs hover:underline"
            onClick={() =>
              setSelectedTimeFrames(new Set(sectionSlotsUniqueTimeFrames))
            }
          >
            {t('generic.selectAll')}
          </button>
          <button
            type="button"
            className="block text-red-700 mb-1 text-xs hover:underline"
            onClick={() => setSelectedTimeFrames(new Set())}
          >
            {t('generic.clear')}
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-3 ml-2">
        {sectionSlotsUniqueTimeFrames.map(timeFrame => (
          <span
            key={timeFrame}
            className="inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200"
          >
            <input
              type="checkbox"
              checked={selectedTimeFrames.has(timeFrame)}
              onChange={() => {
                setSelectedTimeFrames(prev => {
                  const newSet = new Set(prev);
                  if (prev.has(timeFrame)) {
                    newSet.delete(timeFrame);
                  } else {
                    newSet.add(timeFrame);
                  }
                  return newSet;
                });
              }}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            {timeFrame}
          </span>
        ))}
      </div>

      {/* Apply selection button */}
      <YSButton onClick={handleSmartSectionSlotsSelector} classNames="mt-2">
        {t('manager.preferencesConfig.applySelection')}
      </YSButton>

      {/* Selected slots display */}
      <div className="mt-4">
        <div className="w-full h-px bg-gray-400 my-2" />
        <h3 className="text-gray-600 mb-2">
          3. {t('manager.preferencesConfig.selectedSlotsTitle')}
        </h3>
        <p className="text-xs text-gray-600 mb-2">
          {t('manager.preferencesConfig.selectedSlotsDescription')}
        </p>
        <div className="max-h-40 overflow-y-auto border rounded p-2 space-y-1">
          {selectedSlots.size === 0 ? (
            <p className="text-sm text-gray-500 italic">
              {t('manager.preferencesConfig.noSlotsSelected')}
            </p>
          ) : (
            Array.from(selectedSlots)
              .sort((a, b) => {
                const slotA = sectionSlotsDict[a];
                const slotB = sectionSlotsDict[b];
                return (
                  new Date(slotA.start).getTime() -
                  new Date(slotB.start).getTime()
                );
              })
              .map(slotId => {
                const slot = sectionSlotsDict[slotId];
                return slot ? (
                  <CompactSectionSlotChip
                    key={slot.id_section_slot}
                    sectionSlot={slot}
                    section={sections[slot.id_section]}
                    showDay
                    locale={normalizedLocale}
                  />
                ) : null;
              })
          )}
        </div>
      </div>
    </div>
  );
}
